/**
 * Submap interno de um POP: nodes = devices do POP, edges = DeviceLink.
 * Layout dagre + click no node abre detail/edit; botão criar link entre 2 devices.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ReactFlow, Background, Controls, MiniMap, Handle, Position, type Edge, type Node, type NodeProps } from '@xyflow/react';
import dagre from '@dagrejs/dagre';
import '@xyflow/react/dist/style.css';
import { api, type Pop, type Device, type DeviceLink } from './api';
import { LinkLivePanel } from './components/LinkLivePanel';

const STATE_COLORS: Record<string, string> = { UP: '#10b981', DEGRADED: '#f59e0b', DOWN: '#ef4444', UNKNOWN: '#64748b' };
const ROLE_BG: Record<string, string> = { ROUTER: '#0ea5e9', SWITCH: '#a855f7', OLT: '#84cc16', FIREWALL: '#dc2626', SERVER: '#475569' };

type DeviceNodeData = { device: Device };

function DeviceNode({ data }: NodeProps<Node<DeviceNodeData>>) {
  const d = data.device;
  const bg = ROLE_BG[d.role] ?? '#334155';
  return (
    <div style={{ borderRadius: 6, background: '#0f172a', border: '1px solid #334155', padding: '6px 8px', width: 180, boxShadow: `0 0 0 2px ${bg}33` }}>
      <Handle type="target" position={Position.Left} style={{ background: bg }} />
      <Handle type="source" position={Position.Right} style={{ background: bg }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10 }}>
        <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 99, background: bg }} />
        <span style={{ textTransform: 'uppercase', color: '#64748b' }}>{d.role}</span>
        <span style={{ marginLeft: 'auto', color: '#64748b' }}>{d.vendor?.code ?? ''}</span>
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {d.hostname.replace(/^(RTR|SWT|OLT|RTF|SRV) - /, '')}
      </div>
      <div style={{ fontSize: 10, color: '#94a3b8', fontFamily: 'monospace' }}>{d.mgmtIp}</div>
    </div>
  );
}

const nodeTypes = { device: DeviceNode };

function layoutDagre(nodes: Node[], edges: Edge[]) {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'LR', nodesep: 50, ranksep: 80 });
  g.setDefaultEdgeLabel(() => ({}));
  nodes.forEach((n) => g.setNode(n.id, { width: 190, height: 60 }));
  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);
  return nodes.map((n) => {
    const p = g.node(n.id);
    return { ...n, position: { x: (p?.x ?? 0) - 95, y: (p?.y ?? 0) - 30 } };
  });
}

export function PopSubmap({ pop, onClose, canWrite }: { pop: Pop; onClose: () => void; canWrite: boolean }) {
  const qc = useQueryClient();
  const devicesQ = useQuery({ queryKey: ['devices-of-pop-submap', pop.id], queryFn: () => api.devices({ popId: pop.id, take: 200 }) });
  const linksQ   = useQuery({ queryKey: ['device-links', pop.id], queryFn: () => api.deviceLinks(pop.id), refetchInterval: 30_000 });

  const devices: Device[] = (devicesQ.data?.devices ?? []) as any;
  const links: DeviceLink[] = linksQ.data?.links ?? [];

  const { nodes, edges } = useMemo(() => {
    const ns: Node[] = devices.map((d) => ({
      id: String(d.id),
      type: 'device',
      data: { device: d },
      position: { x: 0, y: 0 }
    }));
    const es: Edge[] = links.map((l) => {
      const pending = l.approved === false;
      const baseLabel = [l.ifaceAName, l.ifaceBName].filter(Boolean).join(' ↔ ');
      return {
        id: String(l.id),
        source: String(l.deviceAId),
        target: String(l.deviceBId),
        label: (pending ? '⏳ ' : '') + (baseLabel || ''),
        style: {
          stroke: pending ? '#f59e0b' : (STATE_COLORS[l.state] || STATE_COLORS.UNKNOWN),
          strokeWidth: 2,
          strokeDasharray: pending ? '6 4' : undefined
        },
        labelStyle: { fill: pending ? '#fbbf24' : '#e2e8f0', fontSize: 10 },
        labelBgStyle: { fill: '#0f172a' },
        data: { link: l }
      };
    });
    return { nodes: layoutDagre(ns, es), edges: es };
  }, [devices, links]);

  const [creating, setCreating] = useState(false);
  const [selectedLink, setSelectedLink] = useState<DeviceLink | null>(null);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(2,6,23,0.97)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 16px', borderBottom: '1px solid #1e293b' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#f1f5f9' }}>{pop.uf ? `[${pop.uf}] ` : ''}{pop.name} · submap interno</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>{devices.length} devices · {links.length} enlaces internos</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canWrite && devices.length >= 2 && (
            <button onClick={() => setCreating(true)} style={{ padding: '6px 12px', fontSize: 12, background: '#047857', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>+ Novo link intra-POP</button>
          )}
          <button onClick={onClose} style={{ padding: '6px 12px', fontSize: 12, background: '#334155', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>fechar</button>
        </div>
      </div>
      <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
        {devicesQ.isLoading ? (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>carregando…</div>
        ) : devices.length === 0 ? (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>nenhum device cadastrado neste POP</div>
        ) : (
          <ReactFlow nodes={nodes} edges={edges} nodeTypes={nodeTypes} fitView style={{ background: '#020617' }} onEdgeClick={(_, edge) => setSelectedLink((edge as any).data?.link ?? null)}>
            <Background color="#334155" gap={24} />
            <Controls />
            <MiniMap pannable zoomable style={{ background: '#0f172a' }} />
          </ReactFlow>
        )}
        {selectedLink && (
          <DeviceLinkDialog link={selectedLink} devices={devices} onClose={() => setSelectedLink(null)} onChanged={() => { setSelectedLink(null); qc.invalidateQueries({ queryKey: ['device-links', pop.id] }); }} canWrite={canWrite} />
        )}
        {creating && (
          <NewDeviceLinkForm
            devices={devices}
            onClose={() => setCreating(false)}
            onCreated={() => { setCreating(false); qc.invalidateQueries({ queryKey: ['device-links', pop.id] }); }}
          />
        )}
      </div>
    </div>
  );
}

function NewDeviceLinkForm({ devices, onClose, onCreated }: { devices: Device[]; onClose: () => void; onCreated: () => void }) {
  const [a, setA] = useState<number>(devices[0]?.id ?? 0);
  const [b, setB] = useState<number>(devices[1]?.id ?? 0);
  const [ifA, setIfA] = useState('');
  const [ifB, setIfB] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const ifAQ = useQuery({ queryKey: ['zbx-ifaces', a], queryFn: () => api.zbxInterfaces(a), enabled: !!a });
  const ifBQ = useQuery({ queryKey: ['zbx-ifaces', b], queryFn: () => api.zbxInterfaces(b), enabled: !!b });
  useEffect(() => setIfA(''), [a]);
  useEffect(() => setIfB(''), [b]);
  const m = useMutation({
    mutationFn: () => api.deviceLinkCreate({ deviceAId: a, deviceBId: b, ifaceAName: ifA || undefined, ifaceBName: ifB || undefined, type: 'MANUAL' }),
    onSuccess: () => onCreated(),
    onError: (e: any) => setErr(e.detail?.error === 'duplicate_link' ? 'já existe esse par' : (e.detail?.detail ?? e.message ?? 'erro'))
  });
  const ifacesA = ifAQ.data?.interfaces ?? [];
  const ifacesB = ifBQ.data?.interfaces ?? [];
  const sel: React.CSSProperties = { width: '100%', background: '#020617', border: '1px solid #334155', borderRadius: 4, padding: '6px 8px', fontSize: 12, color: '#e2e8f0' };
  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(2,6,23,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
      <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, padding: 16, width: 640 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9', marginBottom: 12 }}>Novo enlace intra-POP</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 11, color: '#10b981' }}>▶ Device A</div>
            <select style={sel} value={a} onChange={(e) => setA(Number(e.target.value))}>
              {devices.map((d) => <option key={d.id} value={d.id}>{d.hostname}</option>)}
            </select>
            <select style={sel} value={ifA} onChange={(e) => setIfA(e.target.value)}>
              <option value="">— sem iface —</option>
              {ifacesA.map((i) => <option key={i.name} value={i.name}>{i.name}{i.description ? ` · ${i.description}` : ''}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 11, color: '#f43f5e' }}>◀ Device B</div>
            <select style={sel} value={b} onChange={(e) => setB(Number(e.target.value))}>
              {devices.filter((d) => d.id !== a).map((d) => <option key={d.id} value={d.id}>{d.hostname}</option>)}
            </select>
            <select style={sel} value={ifB} onChange={(e) => setIfB(e.target.value)}>
              <option value="">— sem iface —</option>
              {ifacesB.map((i) => <option key={i.name} value={i.name}>{i.name}{i.description ? ` · ${i.description}` : ''}</option>)}
            </select>
          </div>
        </div>
        {err && <div style={{ color: '#f43f5e', fontSize: 12, marginTop: 8 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 12, marginTop: 12, borderTop: '1px solid #1e293b' }}>
          <button onClick={onClose} style={{ padding: '6px 12px', fontSize: 12, background: 'transparent', color: '#94a3b8', border: 'none', cursor: 'pointer' }}>cancelar</button>
          <button disabled={m.isPending || a === b} onClick={() => { setErr(null); m.mutate(); }}
                  style={{ padding: '6px 12px', fontSize: 12, background: '#047857', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', opacity: (m.isPending || a === b) ? 0.5 : 1 }}>
            {m.isPending ? 'criando…' : 'criar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeviceLinkDialog({ link, devices, onClose, onChanged, canWrite }: { link: DeviceLink; devices: Device[]; onClose: () => void; onChanged: () => void; canWrite: boolean }) {
  const approve = useMutation({ mutationFn: () => api.deviceLinkApprove(link.id), onSuccess: onChanged });
  const del = useMutation({ mutationFn: () => api.deviceLinkDelete(link.id), onSuccess: onChanged });

  // Corrigir pontas (host + interface) — quando vinculou errado.
  const [editEnds, setEditEnds] = useState(false);
  const [devA, setDevA] = useState<number>(link.deviceA.id);
  const [devB, setDevB] = useState<number>(link.deviceB.id);
  const [ifA, setIfA] = useState<string>(link.ifaceAName ?? '');
  const [ifB, setIfB] = useState<string>(link.ifaceBName ?? '');
  const [endErr, setEndErr] = useState<string | null>(null);
  const ifAQ = useQuery({ queryKey: ['zbx-ifaces', devA], queryFn: () => api.zbxInterfaces(devA), enabled: editEnds && !!devA });
  const ifBQ = useQuery({ queryKey: ['zbx-ifaces', devB], queryFn: () => api.zbxInterfaces(devB), enabled: editEnds && !!devB });
  const inList = (id: number) => devices.some((d) => d.id === id);
  const saveEnds = useMutation({
    mutationFn: () => api.deviceLinkUpdate(link.id, { deviceAId: devA, deviceBId: devB, ifaceAName: ifA || null, ifaceBName: ifB || null }),
    onSuccess: onChanged,
    onError: (e: any) => setEndErr(e?.detail?.error === 'duplicate_link' ? 'já existe enlace entre esse par' : (e?.detail?.error ?? e?.message ?? 'erro'))
  });
  const endpointSel: React.CSSProperties = { width: '100%', background: '#020617', color: '#e2e8f0', border: '1px solid #334155', padding: 4, borderRadius: 4, fontSize: 11 };
  // F12 — Operação (swap/transport)
  const linkAny = link as any;
  const [ownership, setOwnership] = useState<'OWN' | 'SWAP' | 'TRANSPORT'>(linkAny.ownership ?? 'OWN');
  const [providerId, setProviderId] = useState<number | null>(linkAny.providerId ?? null);
  const [circuitId, setCircuitId] = useState<string>(linkAny.providerCircuitId ?? '');
  const [costReais, setCostReais] = useState<string>(linkAny.monthlyCostCents != null ? (linkAny.monthlyCostCents / 100).toFixed(2) : '');
  const [opErr, setOpErr] = useState<string | null>(null);
  const opProvidersQ = useQuery({
    queryKey: ['providers', 'swap-transport'],
    queryFn: async () => {
      const [swap, transp] = await Promise.all([
        api.providers({ kind: 'SWAP_FIBER', enabled: true }),
        api.providers({ kind: 'TRANSPORT', enabled: true })
      ]);
      return { providers: [...swap.providers, ...transp.providers] };
    },
    enabled: canWrite
  });
  const saveOp = useMutation({
    mutationFn: () => {
      const body: any = { ownership };
      if (ownership === 'OWN') {
        body.providerId = null; body.providerCircuitId = null; body.monthlyCostCents = null;
      } else {
        body.providerId = providerId;
        body.providerCircuitId = circuitId.trim() || null;
        body.monthlyCostCents = costReais ? Math.round(Number(costReais) * 100) : null;
      }
      return api.deviceLinkUpdate(link.id, body);
    },
    onSuccess: onChanged,
    onError: (e: any) => setOpErr(e?.detail?.error ?? e?.message ?? 'erro')
  });
  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(2,6,23,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
      <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, padding: 16, width: 520 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9', marginBottom: 4 }}>Enlace intra-POP #{link.id}</div>
        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
          {link.deviceA.hostname} <span style={{ color: '#64748b' }}>↔</span> {link.deviceB.hostname}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11, color: '#cbd5e1' }}>
          <div><span style={{ color: '#64748b' }}>iface A:</span> {link.ifaceAName ?? '—'}</div>
          <div><span style={{ color: '#64748b' }}>iface B:</span> {link.ifaceBName ?? '—'}</div>
          <div><span style={{ color: '#64748b' }}>state:</span> <span style={{ color: { UP: '#10b981', DEGRADED: '#f59e0b', DOWN: '#ef4444', UNKNOWN: '#64748b' }[link.state] }}>{link.state}</span></div>
          <div><span style={{ color: '#64748b' }}>type:</span> {link.type}</div>
          <div><span style={{ color: '#64748b' }}>source:</span> {(link as any).source ?? 'MANUAL'}</div>
          <div><span style={{ color: '#64748b' }}>approved:</span> <span style={{ color: link.approved === false ? '#fbbf24' : '#10b981' }}>{link.approved === false ? '⏳ pendente' : 'sim'}</span></div>
        </div>
        {link.approved === false && (
          <div style={{ marginTop: 12, padding: 8, background: 'rgba(245,158,11,0.1)', border: '1px solid #92400e', borderRadius: 4, fontSize: 11, color: '#fbbf24' }}>
            Esta sugestão veio do {(link as any).source}. Aprove pra confirmar, ou exclua se for falso-positivo.
          </div>
        )}
        {/* Corrigir pontas (host + interface) — quando vinculou errado */}
        {canWrite && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #1e293b' }}>
            {!editEnds ? (
              <button onClick={() => setEditEnds(true)}
                      style={{ padding: '4px 10px', fontSize: 11, background: 'transparent', color: '#7dd3fc', border: '1px solid #334155', borderRadius: 4, cursor: 'pointer' }}>
                ✏ corrigir host / interface
              </button>
            ) : (
              <>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#cbd5e1', marginBottom: 6 }}>✏ Corrigir pontas</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ fontSize: 11, color: '#10b981' }}>▶ Ponta A</div>
                    <select style={endpointSel} value={devA} onChange={(e) => { setDevA(Number(e.target.value)); setIfA(''); }}>
                      {!inList(devA) && <option value={devA}>{link.deviceA.hostname} (fora do POP)</option>}
                      {devices.filter((d) => d.id !== devB).map((d) => <option key={d.id} value={d.id}>{d.hostname}</option>)}
                    </select>
                    <select style={endpointSel} value={ifA} onChange={(e) => setIfA(e.target.value)}>
                      <option value="">— sem iface —</option>
                      {ifA && !(ifAQ.data?.interfaces ?? []).some((i) => i.name === ifA) && <option value={ifA}>{ifA}</option>}
                      {(ifAQ.data?.interfaces ?? []).map((i) => <option key={i.name} value={i.name}>{i.name}{i.description ? ` · ${i.description}` : ''}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ fontSize: 11, color: '#f43f5e' }}>◀ Ponta B</div>
                    <select style={endpointSel} value={devB} onChange={(e) => { setDevB(Number(e.target.value)); setIfB(''); }}>
                      {!inList(devB) && <option value={devB}>{link.deviceB.hostname} (fora do POP)</option>}
                      {devices.filter((d) => d.id !== devA).map((d) => <option key={d.id} value={d.id}>{d.hostname}</option>)}
                    </select>
                    <select style={endpointSel} value={ifB} onChange={(e) => setIfB(e.target.value)}>
                      <option value="">— sem iface —</option>
                      {ifB && !(ifBQ.data?.interfaces ?? []).some((i) => i.name === ifB) && <option value={ifB}>{ifB}</option>}
                      {(ifBQ.data?.interfaces ?? []).map((i) => <option key={i.name} value={i.name}>{i.name}{i.description ? ` · ${i.description}` : ''}</option>)}
                    </select>
                  </div>
                </div>
                {endErr && <div style={{ color: '#f43f5e', fontSize: 11, marginTop: 4 }}>{endErr}</div>}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={() => { setEndErr(null); saveEnds.mutate(); }} disabled={saveEnds.isPending || devA === devB}
                          style={{ padding: '6px 12px', fontSize: 12, background: '#047857', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', opacity: (saveEnds.isPending || devA === devB) ? 0.5 : 1 }}>
                    {saveEnds.isPending ? 'salvando…' : 'salvar pontas'}
                  </button>
                  <button onClick={() => { setEditEnds(false); setEndErr(null); setDevA(link.deviceA.id); setDevB(link.deviceB.id); setIfA(link.ifaceAName ?? ''); setIfB(link.ifaceBName ?? ''); }}
                          style={{ padding: '6px 12px', fontSize: 12, background: 'transparent', color: '#94a3b8', border: 'none', cursor: 'pointer' }}>cancelar</button>
                </div>
              </>
            )}
          </div>
        )}

        {link.ifaceAName && link.ifaceBName && (
          <LinkLivePanel linkId={link.id} kind="device" />
        )}

        {/* F12 — Operação */}
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #1e293b' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#cbd5e1', marginBottom: 6 }}>🔧 Operação</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11 }}>
            <div>
              <label style={{ display: 'block', color: '#64748b', marginBottom: 2 }}>Tipo do enlace</label>
              <select value={ownership} onChange={(e) => setOwnership(e.target.value as any)}
                      disabled={!canWrite}
                      style={{ width: '100%', background: '#020617', color: '#e2e8f0', border: '1px solid #334155', padding: 4, borderRadius: 4 }}>
                <option value="OWN">Próprio (fibra SC)</option>
                <option value="SWAP">Swap (terceiro)</option>
                <option value="TRANSPORT">Transporte (operadora)</option>
              </select>
            </div>
            {ownership !== 'OWN' && (
              <>
                <div>
                  <label style={{ display: 'block', color: '#64748b', marginBottom: 2 }}>Fornecedor</label>
                  <select value={providerId ?? ''} onChange={(e) => setProviderId(e.target.value ? Number(e.target.value) : null)}
                          disabled={!canWrite}
                          style={{ width: '100%', background: '#020617', color: '#e2e8f0', border: '1px solid #334155', padding: 4, borderRadius: 4 }}>
                    <option value="">— escolha —</option>
                    {opProvidersQ.data?.providers.map((p) => (
                      <option key={p.id} value={p.id}>{p.code}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', color: '#64748b', marginBottom: 2 }}>Circuito</label>
                  <input value={circuitId} onChange={(e) => setCircuitId(e.target.value)} disabled={!canWrite}
                         placeholder="CIR-12345"
                         style={{ width: '100%', background: '#020617', color: '#e2e8f0', border: '1px solid #334155', padding: 4, borderRadius: 4 }} />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#64748b', marginBottom: 2 }}>Custo mensal (R$)</label>
                  <input type="number" step="0.01" value={costReais} onChange={(e) => setCostReais(e.target.value)} disabled={!canWrite}
                         placeholder="2500.00"
                         style={{ width: '100%', background: '#020617', color: '#e2e8f0', border: '1px solid #334155', padding: 4, borderRadius: 4 }} />
                </div>
              </>
            )}
          </div>
          {opErr && <div style={{ color: '#f43f5e', fontSize: 11, marginTop: 4 }}>{opErr}</div>}
          {canWrite && (
            <button onClick={() => { setOpErr(null); saveOp.mutate(); }} disabled={saveOp.isPending}
                    style={{ marginTop: 8, padding: '6px 12px', fontSize: 12, background: '#047857', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
              {saveOp.isPending ? 'salvando…' : 'salvar operação'}
            </button>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, paddingTop: 12, marginTop: 12, borderTop: '1px solid #1e293b' }}>
          {canWrite && (
            <button onClick={() => { if (confirm('Excluir esse enlace?')) del.mutate(); }} disabled={del.isPending}
                    style={{ padding: '6px 12px', fontSize: 12, background: '#9f1239', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
              🗑️ excluir
            </button>
          )}
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <button onClick={onClose} style={{ padding: '6px 12px', fontSize: 12, background: 'transparent', color: '#94a3b8', border: 'none', cursor: 'pointer' }}>fechar</button>
            {canWrite && link.approved === false && (
              <button onClick={() => approve.mutate()} disabled={approve.isPending}
                      style={{ padding: '6px 12px', fontSize: 12, background: '#047857', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                ✓ aprovar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
